import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, ServiceResource } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-service-resources',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('catalog.service.resources.title')" [description]="t('catalog.service.resources.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="showCreate.set(!showCreate())"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>

          @if (showCreate()) {
            <section class="create-form">
              <label>
                <span>{{ t('catalog.service.resourceCode') }}</span>
                <input pInputText type="text" [(ngModel)]="form.code" />
              </label>
              <label>
                <span>{{ t('catalog.service.resourceName') }}</span>
                <input pInputText type="text" [(ngModel)]="form.name" />
              </label>
              <label>
                <span>{{ t('catalog.service.resourceType') }}</span>
                <input pInputText type="text" [(ngModel)]="form.resourceType" />
              </label>
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="saving()" (click)="save()"></button>
            </section>
          }

          <p-table [value]="rows()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.service.resourceCode') }}</th>
                <th>{{ t('catalog.service.resourceName') }}</th>
                <th>{{ t('catalog.service.resourceType') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.code }}</td>
                <td>{{ row.name }}</td>
                <td>{{ row.resourceType }}</td>
                <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; }
    .create-form { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; align-items: end; }
    .create-form label { display: grid; gap: 6px; }
    .create-form span { font-size: 12px; color: #475569; font-weight: 600; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class ServiceResourcesComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly showCreate = signal(false);
  readonly error = signal('');
  readonly rows = signal<ServiceResource[]>([]);

  readonly form = { code: '', name: '', resourceType: 'Technician' };

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.service.listServiceResources().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  save(): void {
    if (!this.form.code.trim() || !this.form.name.trim() || !this.form.resourceType.trim()) {
      this.error.set(this.t('catalog.service.resources.requiredFields'));
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.service.createServiceResource({
      code: this.form.code.trim(),
      name: this.form.name.trim(),
      resourceType: this.form.resourceType.trim()
    }).subscribe({
      next: created => {
        this.rows.set([created, ...this.rows()]);
        this.form.code = '';
        this.form.name = '';
        this.form.resourceType = 'Technician';
        this.showCreate.set(false);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.service.resources.createFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
